/**
 * The rows an owner manages after publishing: a tee-time sheet, this week's lessons, a rota.
 *
 * The web's own client for the phone's src/webapp/managedRows.ts, and the same contract: the
 * rows live on the server under `/apps/{hostedAppId}/{collection}`, declared `ownerWrite` — anyone
 * may read, only the owner may write — so a change is live for every visitor on their next look,
 * with no republish. Fetched through `request()` rather than imported, for the reason api/owner.ts
 * gives: the phone's module takes its token from CognitoService, which reaches expo-secure-store
 * and so React Native.
 *
 * `columnsOf` is shared rather than copied: it lives in @/webapp/managedColumns, which imports
 * nothing.
 */
import { ApiError, request, type RequestOptions } from './client';

export interface ManagedRow {
  id: string;
  createdAt?: string;
  [field: string]: unknown;
}

/** The phone's sentences for the two refusals that are about the app, not the moment. */
export const NOT_PUBLISHED = 'Publish this app first — these rows live with the published copy.';
export const NOT_ALLOWED = 'Only the owner of an app can change what it shows.';

function path(hostedAppId: string | undefined, collection: string, id?: string): string {
  if (!hostedAppId) throw new ApiError('misconfigured', NOT_PUBLISHED);
  return `/apps/${encodeURIComponent(hostedAppId)}/${encodeURIComponent(collection)}${id ? `/${encodeURIComponent(id)}` : ''}`;
}

/**
 * A 403 here means the signed-in account is not this app's owner, and the client's own words
 * for it ("You do not have access to that.") do not say what to do. Every other failure keeps
 * the client's sentence — offline, a timeout and an expired session each say more than the
 * phone's single "That didn't go through".
 */
async function call<T>(url: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await request<T>(url, options);
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      throw new ApiError('forbidden', NOT_ALLOWED, err.status, err.reason, { code: err.code, takenDown: err.takenDown });
    }
    throw err;
  }
}

const PAGE = 200;
/** 6000 rows, far past any real list, and a bound so a stuck cursor cannot hang the editor. */
const MAX_PAGES = 30;

/**
 * Every row, oldest first — a list the owner keeps reads in the order it was made, not
 * newest-first like replies. Follows the cursor to the end, and says when it had to stop short
 * (`complete: false`) while still handing over what it did load: the phone learned both halves
 * — a 200-row cap that said nothing, then a refusal that left nothing editable at all.
 *
 * Sorted once, across the whole set: sorting per page would interleave wrongly.
 */
export async function fetchManagedRows(
  hostedAppId: string | undefined,
  collection: string,
): Promise<{ rows: ManagedRow[]; complete: boolean }> {
  const url = path(hostedAppId, collection);
  const all: ManagedRow[] = [];
  const used = new Set<string>();
  let cursor: string | undefined;
  let complete = true;

  for (let i = 0; i < MAX_PAGES; i++) {
    const query = `?limit=${PAGE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
    const body = await call<{ items?: ManagedRow[]; cursor?: string } | undefined>(`${url}${query}`);
    all.push(...(body?.items ?? []));
    if (!body?.cursor) break;
    // A cursor that does not advance is a server bug, and it must not become a hang.
    if (used.has(body.cursor)) {
      complete = false;
      break;
    }
    used.add(body.cursor);
    cursor = body.cursor;
    // Ran out of pages before running out of rows.
    if (i === MAX_PAGES - 1) complete = false;
  }

  return {
    rows: all.slice().sort((a, b) => String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''))),
    complete,
  };
}

export async function addManagedRow(
  hostedAppId: string | undefined,
  collection: string,
  row: Record<string, unknown>,
): Promise<ManagedRow> {
  return call<ManagedRow>(path(hostedAppId, collection), { method: 'POST', body: row });
}

export async function updateManagedRow(
  hostedAppId: string | undefined,
  collection: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<ManagedRow> {
  return call<ManagedRow>(path(hostedAppId, collection, id), { method: 'PUT', body: patch });
}

export async function removeManagedRow(hostedAppId: string | undefined, collection: string, id: string): Promise<void> {
  await call<unknown>(path(hostedAppId, collection, id), { method: 'DELETE' });
}

/** The columns to offer, learned from the rows that exist — the phone's own rule, shared. */
export { columnsOf } from '@/webapp/managedColumns';
